import { later } from '@ember/runloop';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { cached } from '@glimmer/tracking';

import moment from 'moment';

import type {
	BoardPreviewMap,
	ImageCropData,
	PopupListItem,
	RecordData,
	RequestError,
	Service,
} from 'webapp-types';
import {
	BillingFeature,
	BoardPopupShareTab,
	DesignerLayout,
	ModelName,
	SourceStatusLevel,
	UserAccess,
} from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { compact, equal, getSourcesTexts, uniq } from 'webapp/utils/common';
import { getFormattedTime, getRelativeTime } from 'webapp/utils/number';
import { isVerified, type VerificationInfo } from 'webapp/utils/verification';

import type { BlockPreviewRecord } from 'webapp/models';
import type Block from 'webapp/models-custom/block';
import type { Changes as BoardChanges } from 'webapp/models-custom/board-state';
import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import DatawallGrid from 'webapp/models-custom/datawall-grid';
import DateRange from 'webapp/models-custom/date-range';
import type ResolutionCenterItem from 'webapp/models-custom/resolutioncenter-item';
import type Source from 'webapp/models-custom/source';
import type SourceType from 'webapp/models-custom/source-type';
import type Template from 'webapp/models-custom/template';
import type BlockRecord from 'webapp/models/block';
import type BoardRecord from 'webapp/models/board';
import type BoardPreviewRecord from 'webapp/models/board-preview';
import type SourceRecord from 'webapp/models/source';
import type TemplateRecord from 'webapp/models/template';
import type UserRecord from 'webapp/models/user';

import type Account from 'webapp/services/account';
import type Billing from 'webapp/services/billing';
import type Constants from 'webapp/services/constants';
import type CurrentUser from 'webapp/services/current-user';
import { Severity } from 'webapp/services/logger';
import type {
	TrackingOriginType as BoardsTrackingOriginType,
	TrackingOriginForTemplateCreated,
} from 'webapp/services/model-manager/boards';

export const BoardStatus = {
	OK: 0,
	// FETCHING: 1,
	ERROR_SOURCE: 2,
	ERROR_RESOLUTION_CENTER: 3,
	EMPTY: -1,
};

export type TrackingOriginForDataboardDuplicate = 'home' | 'databoards' | 'designer';
export type TrackingOriginForDataboardDelete = TrackingOriginForDataboardDuplicate;
export type TrackingOriginForFavoriteBoardAdded = 'databoards' | 'designer';

export type Action =
	| 'openWallShareSettings'
	| 'openFullscreenView'
	| 'openPublicTemplateEditPopup'
	| 'copyBoard'
	| 'createNotification'
	| 'openMigrationTool'
	| 'addToReport'
	| 'addToLoop'
	| 'addToTeam'
	| 'openAccountTemplateEditPopup'
	| 'toggleFavorite'
	| 'transferBoard'
	| 'transferBoardToAgency'
	| 'toggleMobileVisibility'
	| 'openChangeOwnerPopup'
	| 'verify'
	| 'deleteBoard'
	| 'deletePermanently'
	| 'restore';

export default class Board extends BufferedModel<
	BoardRecord | BoardPreviewRecord,
	RecordData.Board.Serialized
> {
	@service('request') declare requestService: Service.Request;
	@service declare infolayer: Service.Infolayer;
	@service('popup') declare popupService: Service.Popup;
	@service('logger') declare loggerService: Service.Logger;
	@service declare timer: Service.Timer;
	@service declare garbageCollector: Service.GarbageCollector;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service declare billing: Billing;
	@service('account') declare accountService: Account;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('app-notifications') declare appNotificationsService: Service.AppNotifications;
	@service declare currentUser: CurrentUser;
	@service declare constants: Constants;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('model-manager/board-previews')
	declare mmBoardPreviewsService: Service.ModelManager.BoardPreviews;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;
	@service('model-manager/block-previews')
	declare mmBlockPreviewsService: Service.ModelManager.BlockPreviews;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;
	@service('model-manager/data-performance-summaries')
	declare mmDataPerformanceSummariesService: Service.ModelManager.DataPerformanceSummaries;
	@service('flow/promotions') declare flowPromotionsService: Service.Flow.Promotions;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/goals')
	declare mmGoalsService: Service.ModelManager.Goals;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;

	// ------- props from / to ED record ------

	get id(): string | null {
		return this.model.id;
	}

	get name(): string {
		return this.getBuffered('name');
	}

	set name(value: string) {
		this.setBuffered('name', value);
	}

	get color(): string {
		return this.getBuffered('color');
	}

	set color(value: string) {
		this.setBuffered('color', value);
	}

	get themeId(): number {
		return this.getBuffered('themeId');
	}

	set themeId(value: number) {
		this.setBuffered('themeId', value);
	}

	get order(): number {
		return this.getBuffered('order');
	}

	set order(value: number) {
		this.setBuffered('order', value);
	}

	get users(): number[] {
		return this.getBuffered('users');
	}

	set users(value: number[]) {
		this.setBuffered('users', value);
	}

	get sources(): RecordData.Board.Source[] {
		return this.getBuffered('sources');
	}

	set sources(value: RecordData.Board.Source[]) {
		this.setBuffered('sources', value);
	}

	get isRandom(): boolean {
		return this.getBuffered('isRandom');
	}

	set isRandom(value: boolean) {
		this.setBuffered('isRandom', value);
	}

	get favorite(): boolean {
		return this.getBuffered('favorite');
	}

	set favorite(value: boolean) {
		this.setBuffered('favorite', value);
	}

	get created(): number {
		return this.model.created;
	}

	get createdById(): number {
		return this.model.createdById;
	}

	get ownerId(): number | null | undefined {
		return this.getBuffered('ownerId');
	}

	set ownerId(value: number) {
		this.setBuffered('ownerId', value);
	}

	get modifiedById(): number {
		return this.model.modifiedById;
	}

	get lastSync(): number {
		return this.model.lastSync;
	}

	get fromTemplate(): number | null {
		return this.getBuffered('fromTemplate');
	}

	set fromTemplate(value: number | null) {
		this.setBuffered('fromTemplate', value);
	}

	get originateFrom(): number | null {
		return this.getBuffered('originateFrom');
	}

	set originateFrom(value: number | null) {
		this.setBuffered('originateFrom', value);
	}

	get token(): string {
		return this.getBuffered('token');
	}

	set token(value: string) {
		this.setBuffered('token', value);
	}

	get isPublicEnabled(): boolean {
		return this.getBuffered('isPublicEnabled');
	}

	set isPublicEnabled(value: boolean) {
		this.setBuffered('isPublicEnabled', value);
	}

	get image(): string {
		return this.getBuffered('image');
	}

	set image(value: string) {
		this.setBuffered('image', value);
	}

	get tags(): number[] {
		return this.getBuffered('tags');
	}

	set tags(value: number[]) {
		this.setBuffered('tags', value);
	}

	get verificationInfo(): VerificationInfo | undefined {
		return this.model.verificationInfo;
	}

	get isVerified(): boolean {
		return isVerified(this.verificationInfo);
	}

	get primaryView(): number {
		return this.getBuffered('primaryView');
	}

	set primaryView(value: number) {
		this.setBuffered('primaryView', value);
	}

	get logo(): string {
		return this.getBuffered('logo');
	}

	set logo(value: string) {
		this.setBuffered('logo', value);
	}

	get logoOriginal(): string {
		return this.getBuffered('logoOriginal');
	}

	set logoOriginal(value: string) {
		this.setBuffered('logoOriginal', value);
	}

	get logoCrop(): ImageCropData | null {
		return this.getBuffered('logoCrop');
	}

	set logoCrop(value: ImageCropData | null) {
		this.setBuffered('logoCrop', value);
	}

	get bg(): string {
		return this.getBuffered('bg');
	}

	set bg(value: string) {
		this.setBuffered('bg', value);
	}

	get bgOriginal(): string {
		return this.getBuffered('bgOriginal');
	}

	set bgOriginal(value: string) {
		this.setBuffered('bgOriginal', value);
	}

	get bgCrop(): ImageCropData | null {
		return this.getBuffered('bgCrop');
	}

	set bgCrop(value: ImageCropData | null) {
		this.setBuffered('bgCrop', value);
	}

	get bgOpacity(): number {
		return this.getBuffered('bgOpacity');
	}

	set bgOpacity(value: number) {
		this.setBuffered('bgOpacity', value);
	}

	get restrictIps(): string[] {
		return this.getBuffered('restrictIps');
	}

	set restrictIps(value: string[]) {
		this.setBuffered('restrictIps', value);
	}

	get password(): string {
		return this.getBuffered('password');
	}

	set password(value: string) {
		this.setBuffered('password', value);
	}

	get hidden(): boolean {
		return this.getBuffered('hidden');
	}

	set hidden(value: boolean) {
		this.setBuffered('hidden', value);
	}

	get access(): number {
		return this.getBuffered('access');
	}

	set access(value: number) {
		this.setBuffered('access', value);
	}

	get templateId(): number | null {
		return this.getBuffered('templateId');
	}

	set templateId(value: number | null) {
		this.setBuffered('templateId', value);
	}

	get spaceId(): number {
		return this.model.spaceId;
	}

	get allowAnonymousDateRangeEdit(): boolean {
		return this.getBuffered('allowAnonymousDateRangeEdit');
	}

	set allowAnonymousDateRangeEdit(value: boolean) {
		this.setBuffered('allowAnonymousDateRangeEdit', value);
	}

	get allowAnonymousFilterEdit(): boolean {
		return this.getBuffered('allowAnonymousFilterEdit');
	}

	set allowAnonymousFilterEdit(value: boolean) {
		this.setBuffered('allowAnonymousFilterEdit', value);
	}

	get deleted(): number | null {
		return this.getBuffered('deleted');
	}

	set deleted(value: number | null) {
		this.setBuffered('deleted', value);
	}

	get isSample(): boolean {
		return this.getBuffered('isSample');
	}

	set isSample(value: boolean) {
		this.setBuffered('isSample', value);
	}

	// boardId & previewHash & changes & previewCreatedBy
	// only for preview-board
	get boardId(): number {
		return this.getBuffered('boardId');
	}

	set boardId(value: number) {
		this.setBuffered('boardId', value);
	}

	get previewHash(): string {
		return this.getBuffered('previewHash');
	}

	set previewHash(value: string) {
		this.setBuffered('previewHash', value);
	}

	// passed to data performance summary of type board preview instead of previewHash
	get boardPreviewId(): number | null {
		return this.previewHash ? (this.model as BoardPreviewRecord).boardPreviewId : null;
	}

	get changes(): BoardChanges {
		return this.getBuffered('changes');
	}

	set changes(value: BoardChanges) {
		this.setBuffered('changes', value);
	}

	get previewCreatedBy(): number {
		return this.getBuffered('previewCreatedBy');
	}

	set previewCreatedBy(value: number) {
		this.setBuffered('previewCreatedBy', value);
	}

	get isDemoAccount(): boolean {
		return this.accountService.isDemoAccount;
	}

	get allowAnonymousDateRangeEditOnDatawall(): boolean {
		return this.checkAllowAnonymousEditOnDatawall('allowAnonymousDateRangeEdit');
	}

	get allowAnonymousFilterEditOnDatawall(): boolean {
		return this.checkAllowAnonymousEditOnDatawall('allowAnonymousFilterEdit');
	}

	private checkAllowAnonymousEditOnDatawall(
		propName: 'allowAnonymousDateRangeEdit' | 'allowAnonymousFilterEdit',
	): boolean {
		// extra check for datawall (public url) to take into account
		// reports or loops allowAnonymous<x>Edit setting
		if (this.navigationService.isDatawallApp) {
			// public datawall has just one report or loop
			const modelToCheck =
				this.mmReportsService.reportModels[0] ||
				this.mmBoardLoopsService.boardLoopModels[0] ||
				this;

			// prop on the context model overwrites the prop on the board itself
			return modelToCheck[propName];
		}
		return this[propName];
	}

	// ------- other props ------

	get isPreview(): boolean {
		return !!this.previewHash;
	}

	get isMyPreview(): boolean {
		return (
			!!this.previewCreatedBy && this.previewCreatedBy === this.currentUser.model?.numericId
		);
	}

	get boardIdForPublicUrl(): string {
		return String(this.isPreview ? this.boardId : this.numericId);
	}

	get isForCurrentSpace(): boolean {
		return !this.spaceId || this.spaceId === this.accountService.id;
	}

	get isMine(): boolean {
		return this.createdById === this.currentUser.numericId;
	}

	get isOwnedByMe(): boolean {
		return this.ownerId === this.currentUser.numericId;
	}

	@cached
	get canCurrentUserEditWithoutDeleted(): boolean {
		// is creator or isnt permission level user without deleted
		return (
			(!this.isPublicTemplate || this.accountService.isPublictemplatesSpace) &&
			!this.currentUser.isViewer &&
			(this.isMine || !this.currentUser.isUser) &&
			(!this.spaceId || this.spaceId === this.accountService.id)
		);
	}

	@cached
	get canCurrentUserEdit(): boolean {
		// is creator or isnt permission level user
		return this.canCurrentUserEditWithoutDeleted && !this.deleted;
	}

	get canEditPermissions(): boolean {
		const { model, isAdmin } = this.currentUser;

		return (this.isMine || isAdmin) && !!model && !model.isViewer;
	}

	get isPublic(): boolean {
		return this.access === UserAccess.PUBLIC;
	}

	get isPrivate(): boolean {
		return this.access === UserAccess.PRIVATE;
	}

	get isRestricted(): boolean {
		return this.access === UserAccess.RESTRICTED;
	}

	get favoriteText(): string {
		return this.favorite ? 'Unfavorite' : 'Favorite';
	}

	get createdText(): string {
		return getFormattedTime(this.created, this.currentUser.model?.timezone);
	}

	get ownerUser(): UserRecord | null {
		let user;
		if (this.ownerId) {
			user = this.mmUsersService.findRecord(String(this.ownerId));
			if (!user) {
				this.loggerService.logError(
					'Missing owner for board',
					{
						boardId: this.id,
						userId: this.ownerId,
					},
					Severity.WARNING,
				);
			}
		}
		return user || null;
	}

	get createdBy(): UserRecord | null {
		let user;
		if (this.createdById) {
			user = this.mmUsersService.findRecord(String(this.createdById));
			if (!user && !this.isTemplate) {
				this.loggerService.logError(
					'Missing createdBy for board',
					{
						boardId: this.id,
						userId: this.createdById,
					},
					Severity.WARNING,
				);
			}
		}
		return user || null;
	}

	get modifiedBy(): UserRecord | null {
		let user;
		if (this.modifiedById) {
			user = this.mmUsersService.findRecord(String(this.modifiedById));
			if (!user) {
				this.loggerService.logError(
					'Missing modifiedBy for board',
					{
						boardId: this.id,
						userId: this.modifiedBy,
					},
					Severity.WARNING,
				);
			}
		}
		return user || null;
	}

	get modifiedByText(): string {
		return this.modifiedBy?.name || this.createdBy?.name || '';
	}

	get internalUrl(): string {
		return this.accountService.appUrl + '/databoards/view/' + this.id;
	}

	get datawallUrl(): string {
		return this.accountService.appUrl + '/datawall/' + this.token;
	}

	get relatedResolutionMessages(): ResolutionCenterItem[] {
		return this.appNotificationsService.visibleNotifications.filter((notif) => {
			return notif.boardIds.includes(this.numericId!);
		});
	}

	get freeSpaces(): number {
		return Board.maxBlocks - this.blocks.length;
	}

	@cached
	get tagsObjs() {
		return this.mmTagsService.savedRecords.filter((tag) => {
			return this.tags.includes(+tag.id!);
		});
	}

	get tagsText(): string {
		return this.tagsObjs.map((item) => item.name).join(', ') || '';
	}

	// ----------- Blocks stuff -----------

	_allBlocks: (BlockRecord | BlockPreviewRecord)[] = [];
	@cached
	get allBlocks(): (BlockRecord | BlockPreviewRecord)[] {
		let allBlocks: (BlockRecord | BlockPreviewRecord)[] = [];

		if (this.isPreview) {
			allBlocks = this.mmBlockPreviewsService.allRecords.filter(
				(item) => item.previewHash === this.previewHash,
			);
		} else {
			const boardId = this.id;
			if (boardId) {
				allBlocks = this.mmBlocksService.blockRecordsByBoard[boardId] || [];
			}
		}

		allBlocks = allBlocks.filter((item) => !item.isDeleted);

		// comparing by IDs is not good enough
		// sometimes block records get unloaded, but the cached references keep IDs
		// and we get false negative and cache does not get updated
		if (
			this._allBlocks.length !== allBlocks.length ||
			this._allBlocks.some((s) => !allBlocks.includes(s))
		) {
			this._allBlocks = allBlocks;
		}

		return this._allBlocks;
	}

	_blocks: (BlockRecord | BlockPreviewRecord)[] = [];
	@cached
	get blocks(): (BlockRecord | BlockPreviewRecord)[] {
		const blocks = this.allBlocks;

		// comparing by IDs is not good enough
		// sometimes block records get unloaded, but the cached references keep IDs
		// and we get false negative and cache does not get updated
		if (
			this._blocks.length !== blocks.length ||
			this._blocks.some((s) => !blocks.includes(s))
		) {
			this._blocks = blocks;
		}

		return this._blocks;
	}

	_allBlockObjs: Block[] = [];
	@cached
	get allBlockObjs(): Block[] {
		// compare models not ids, as blocks might still be saving
		const cachedObjs = this._allBlockObjs,
			cachedModels = cachedObjs.map((item) => item.model),
			newModels = this.allBlocks,
			addedModels = newModels.filter((model) => {
				return !cachedModels.includes(model as BlockRecord);
			}),
			removedObjs = cachedObjs.filter((obj) => {
				return !newModels.includes(obj.model);
			});

		if (removedObjs.length > 0) {
			removedObjs.forEach((obj) => {
				const index = this._allBlockObjs.indexOf(obj);
				this._allBlockObjs.splice(index, 1);
			});
		}

		if (addedModels.length > 0) {
			const newBlockObjs = addedModels.map((blockRecord) => {
				return this.mmBlocksService.createModelFromRecord(blockRecord as BlockRecord);
			});
			this._allBlockObjs.push(...newBlockObjs);
		}

		return this._allBlockObjs;
	}

	_blockObjs: Block[] = [];
	@cached
	get blockObjs(): Block[] {
		const blocks = this.allBlockObjs;

		// comparing by IDs is not good enough
		// sometimes block records get unloaded, but the cached references keep IDs
		// and we get false negative and cache does not get updated
		let isDifferent = false;
		if (this._blockObjs.length === blocks.length) {
			// comparing models
			const records = blocks.map((item) => item.model);
			if (this._blockObjs.some((s) => !records.includes(s.model))) {
				isDifferent = true;
			}
		} else {
			isDifferent = true;
		}

		if (isDifferent) {
			this._blockObjs = blocks;
		}

		return this._blockObjs;
	}

	@cached
	get commonBlockDateRange(): DateRange | null {
		let dr: DateRange | null = null;
		this.blockObjs.some((blockCM: Block) => {
			if (!DateRange.isRangeFormulaEmpty(blockCM.dateRange.range)) {
				if (!dr) {
					dr = DateRange.createFromFormula(blockCM.dateRange);
				} else if (!dr.isRangeEqual(blockCM.dateRange)) {
					dr = null;
					return true; // break loop
				}
			}
			return false;
		});
		return dr; // null if common date range not found, date range obj otherwise
	}

	// ----------- Sources stuff -----------
	_sourcesRecords: SourceRecord[] = [];

	@cached
	get sourcesRecords(): SourceRecord[] {
		// from "sources" passed from BE
		const sourceIds = (this.sources || []).map((item) => item.spaceAccessId);

		const sources = this.mmSourcesService.savedRecords.filter((source) => {
			return sourceIds.includes(+source.id!);
		});

		if (
			!equal(
				this._sourcesRecords.map((item) => item.id),
				sources.map((item) => item.id),
			)
		) {
			this._sourcesRecords = sources;
		}

		return this._sourcesRecords;
	}

	_sourcesObjs: Source[] = [];

	@cached
	get sourcesObjs(): Source[] {
		// compare models not ids, though here specifically it does not matter,
		// as we cannot have a source without an id
		const cachedObjs = this._sourcesObjs,
			cachedModels = cachedObjs.map((item) => item.model),
			newModels = this.sourcesRecords,
			addedModels = newModels.filter((model) => {
				return !cachedModels.includes(model);
			}),
			removedObjs = cachedObjs.filter((obj) => {
				return !newModels.includes(obj.model);
			});

		if (removedObjs.length > 0) {
			removedObjs.forEach((obj) => {
				const index = this._sourcesObjs.indexOf(obj);
				this._sourcesObjs.splice(index, 1);
			});
		}

		if (addedModels.length > 0) {
			const sourcesObjs = this.mmSourcesService.createModelsFromRecordsArray(addedModels);
			this._sourcesObjs.push(...sourcesObjs);
		}

		return this._sourcesObjs;
	}

	get specificSourceTypes(): number[] {
		const types = compact(this.sourcesRecords.map((item) => item.type));
		return uniq(types);
	}

	get specificSourceTypesKeys(): string[] {
		const keys = this.sourcesObjs.map((s) => s.sourceType?.key);
		return uniq(keys);
	}

	@cached
	get sourcesList() {
		// listing from parsed allSources
		const sourceIds = (this.sources || []).map((item) => item.spaceAccessId);
		return this.mmSourcesService.sourceList.filter((sourceData) => {
			return sourceIds.includes(+sourceData.value);
		});
	}

	get sourcesInError() {
		return this.sourcesRecords.filter((item) => item.statusLevel === SourceStatusLevel.ERROR);
	}

	get anySourceError(): boolean {
		return this.sourcesInError.length > 0;
	}

	get sourcesResolvable() {
		return this.sourcesRecords.filter((item) => item.statusAction);
	}

	get sourceTypeIdsMissingFromResolutionMessages(): number[] {
		const mgs = this.relatedResolutionMessages
			.map((item) => item.params.dataSourceTypeId)
			.filter((sourceTypeId) => {
				return typeof sourceTypeId === 'number';
			});
		return uniq(mgs);
	}

	@cached
	get sourcesToConnect(): SourceType[] {
		const allSources = this.mmSourcesService.savedRecords,
			toConnect: SourceType[] = [];

		this.sourceTypeIdsMissingFromResolutionMessages.forEach((missingType: number) => {
			const sourceType = this.mmSourceTypesService.findModelById(missingType);
			if (sourceType && !allSources.find((item) => item.type === missingType)) {
				toConnect.push(sourceType);
			}
		});

		return toConnect;
	}

	@cached
	get sourcesToSelect(): SourceType[] {
		const allSources = this.mmSourcesService.savedRecords,
			toSelect: SourceType[] = [];

		this.sourceTypeIdsMissingFromResolutionMessages.forEach((missingType: number) => {
			const sourceType = this.mmSourceTypesService.findModelById(missingType);
			if (sourceType && allSources.find((item) => item.type === missingType)) {
				toSelect.push(sourceType);
			}
		});

		return toSelect;
	}

	get isAnythingToSolve(): boolean {
		return this.sourcesToConnect.length + this.sourcesToSelect.length > 0;
	}

	get isOnlyConnect(): boolean {
		return this.isAnythingToSolve && this.sourcesToSelect.length === 0;
	}

	get isSingleSourceToConnect(): boolean {
		return this.sourcesToConnect.length === 1 && this.sourcesToSelect.length === 0;
	}

	get isMultipleSourcesToConnect(): boolean {
		return this.sourcesToConnect.length > 0 && this.sourcesToSelect.length === 0;
	}

	@cached
	get firstSourcesText(): string {
		return getSourcesTexts(this.sourcesList).first;
	}

	@cached
	get lastSourcesText(): string {
		return getSourcesTexts(this.sourcesList).last;
	}

	@cached
	get sourcesOtherText(): string {
		return getSourcesTexts(this.sourcesList).other;
	}

	@cached
	get sourcesText(): string {
		const sourcesTexts = getSourcesTexts(this.sourcesList);
		return sourcesTexts.first + ' ' + sourcesTexts.other;
	}

	get sourcesTextShort(): string {
		let text;
		if (this.lastSourcesText) {
			// show number of sources
			const count = this.sourcesList.length;
			text = `${count} data sources`;
		} else {
			// less than 3 sources, just show the names
			text = this.firstSourcesText;
		}
		return text;
	}

	get sourcesTextShortTooltip(): string {
		// tooltip for sourcesTextShort
		let tooltipText = '';
		if (this.lastSourcesText) {
			// more than 3 sources
			tooltipText = this.sourcesList.map((item) => item.label).join('<br>');
		}
		return tooltipText;
	}

	getDataForSingleSourceConnect(): { msgId: number | undefined; source: SourceType } | null {
		if (this.isSingleSourceToConnect) {
			const missingSource = this.sourcesToConnect[0]!, // the single one
				msg = this.relatedResolutionMessages.find(
					(item) => item.params?.dataSourceTypeId === missingSource.id,
				);

			if (msg) {
				return {
					msgId: msg.id,
					source: missingSource,
				};
			}
		}
		return null;
	}

	// ----------- Status stuff -----------

	@cached
	get isDataLoading(): boolean {
		const isInMetricInsight = this.metricDetailsService.isMetricInsightsTemporaryId(
				this.numericId?.toString(),
			),
			isInGoalInsight = this.mmGoalsService.isTemporaryFakeId(this.numericId?.toString());

		if (!this.id || isInMetricInsight || isInGoalInsight) {
			return false;
		}
		if (this.isPreview) {
			return !this.mmBoardPreviewsService.isPreviewDataRequested(this.previewHash);
		} else {
			return !this.mmBoardsService.isBoardDataRequested(this.id);
		}
	}

	get isDataIncomplete(): boolean {
		return this.isDataLoading || this.blockObjs.some((b) => b.isDataIncomplete);
	}

	get status(): number {
		if (this.relatedResolutionMessages.length) {
			return BoardStatus.ERROR_RESOLUTION_CENTER;
		} else if (this.anySourceError) {
			return BoardStatus.ERROR_SOURCE;
		} else if (!this.lastSync) {
			return BoardStatus.EMPTY;
		} else {
			return BoardStatus.OK;
		}
	}

	get statusClass(): string {
		switch (this.status) {
			case BoardStatus.OK:
				return 'ok';
			case BoardStatus.ERROR_SOURCE:
			case BoardStatus.ERROR_RESOLUTION_CENTER:
				return 'error';
			default:
				return '';
		}
	}

	get statusText(): string {
		switch (this.status) {
			case BoardStatus.OK:
				return 'live';
			case BoardStatus.ERROR_SOURCE:
			case BoardStatus.ERROR_RESOLUTION_CENTER:
				return 'Connection problem';
			default:
				return '';
		}
	}

	get lastSyncText(): string {
		this.timer.observe();
		let text = '';
		if (BoardStatus.OK === this.status) {
			text = getRelativeTime(this.lastSync);
		}
		return text;
	}

	get deletedText(): string {
		let text = '';
		if (this.deleted) {
			text = getFormattedTime(this.deleted);
		}
		return text;
	}

	// ----------- Template stuff -----------

	get templateRecord(): TemplateRecord | null {
		const templateId = this.templateId;
		let record;
		if (templateId) {
			record = this.mmTemplatesService.findRecord(String(templateId));
		}
		return record || null;
	}

	_templateModel: Template | null = null;

	@cached
	get templateModel(): Template | null {
		const model = this.templateRecord;

		if (model && model?.id !== this._templateModel?.id) {
			this._templateModel = this.mmTemplatesService.createModel(model);
		}

		return this._templateModel;
	}

	get isTemplate(): boolean {
		return !!this.templateRecord;
	}

	get isPublicTemplate(): boolean {
		return !!this.templateRecord?.isPublic;
	}

	get isAccountTemplate(): boolean {
		return this.isTemplate && !this.templateRecord?.isPublic;
	}

	// ------ GA4 transition --------

	get isGA4Migratable(): boolean {
		const GA3SourceTypeId = this.mmSourceTypesService.findModelByKey('GoogleAnalytics')?.id;

		return (
			this.canCurrentUserEdit &&
			this.sourcesList.some((sourceListEntry) => {
				return sourceListEntry.type === GA3SourceTypeId;
			})
		);
	}

	// ------ Presentation: themes, colors, images --------

	get isWallPrimary(): boolean {
		return this.primaryView === DesignerLayout.DESKTOP;
	}

	@cached
	get bgTheme(): string {
		return this.mmBoardsService.getBgTheme(this.color, this.themeId);
	}

	@cached
	get themeMainColorPair(): string[] {
		const colors = this.colorConfig.colors;
		return [colors[0]!, colors[colors.length - 1] || ''];
	}

	@cached
	get colorConfig() {
		return this.mmBoardsService.getColorConfig(this.color, this.themeId || 1);
	}

	@cached
	get colorGradient(): string {
		return this.mmBoardsService.getColorGradient(this.color);
	}

	@cached
	get style() {
		return htmlSafe('background-color:' + this.color);
	}

	@cached
	get shownLogo(): string {
		return this.logo || this.accountService.boardLogo;
	}

	@cached
	get imageMedium(): string {
		let image = '';
		if (this.image) {
			image = Board.snapshotResize(this.image, 'm');
		}
		return image;
	}

	@cached
	get imageMediumOrDefault(): string {
		let image = this.imageMedium;
		if (!image) {
			image =
				this.bgTheme === 'dark' ? '/images/datawall.png' : '/images/datawall-onwhite.png';
		}
		return image;
	}

	get allImages(): string[] {
		const images = [];
		if (this.bg) {
			images.push(this.bg);
		}

		if (this.logo) {
			images.push(this.logo);
		}

		this.blocks.forEach((block) => {
			if (block.imageUrl) {
				images.push(block.imageUrl);
			}
		});

		this.blockObjs.forEach((blockObj) => {
			if (blockObj.model.mediaEnabled) {
				blockObj.sample?.sampledata.dsData.forEach((dsData) => {
					const mediaMap = dsData.mediaMap || {};
					Object.values(mediaMap).forEach((mediaUrl) => {
						images.push(mediaUrl);
					});
				});
			}
		});

		return uniq(images);
	}

	getAdditionalOptions(
		isBoardsPage = true,
		isPreviewPopup = false,
		isTeamSectionItem = false,
	): PopupListItem<Action>[] {
		const options: PopupListItem<Action>[] = [],
			canEdit = this.canCurrentUserEdit;

		if (this.deleted) {
			if (this.canCurrentUserEditWithoutDeleted) {
				options.push({
					class: 'delete-permanently',
					text: 'Delete permanently',
					action: 'deletePermanently',
					icon: 'Trash',
				});
			}
			return options;
		}

		if (!isPreviewPopup) {
			options.push({
				text: 'Share',
				action: 'openWallShareSettings',
				icon: 'Share',
			});

			options.push({
				text: 'Full-screen view',
				action: 'openFullscreenView',
				icon: 'FullScreen',
			});
		}

		if (isBoardsPage) {
			const notificationsOption: PopupListItem<Action> | null =
				this.mmNotificationsService.getNotificationsDropdownOptionForFeature(
					this,
					'createNotification',
				);
			if (notificationsOption) {
				options.push(notificationsOption);
			}
		}

		if (this.accountService.isPublictemplatesSpace) {
			options.push({
				class: 'templates',
				text: this.isTemplate ? 'Edit public template' : 'Save as public template',
				action: 'openPublicTemplateEditPopup',
				icon: 'FolderOpen',
			});
		}

		if (canEdit) {
			if (!isTeamSectionItem) {
				options.push({
					text: 'Duplicate',
					action: 'copyBoard',
					icon: 'Copy',
				});
			}

			if (this.isGA4Migratable) {
				options.push({
					class: 'sparkle migrate-ga4-option',
					text: 'Transition to GA4',
					action: 'openMigrationTool',
					icon: 'Ai',
				});
			}

			options.push({
				text: 'Add to report',
				action: 'addToReport',
				icon: 'Reports',
			});

			options.push({
				text: 'Add to loop',
				action: 'addToLoop',
				icon: 'Loop',
			});

			if (!isTeamSectionItem) {
				options.push({
					text: 'Add to Space',
					action: 'addToTeam',
					icon: 'Spaces',
				});
			}

			options.push({
				text: 'Save as template',
				action: 'openAccountTemplateEditPopup',
				isDisabled: this.isPublicTemplate,
				icon: 'FolderUpload',
				extra: this.billingService.accountTemplatesEnabled
					? undefined
					: {
							type: 'featureLimitation',
						},
			});
		}

		options.push({
			class: this.favorite ? 'accent' : '',
			text: this.favoriteText,
			action: 'toggleFavorite',
			icon: this.favorite ? 'StarFill' : 'Star',
		});

		if (canEdit) {
			options.push({
				icon: 'Verified',
				text: this.isVerified ? 'Unverify' : 'Verify',
				action: 'verify',
				isDisabled: !this.verificationInfo,
			});
		}

		if (canEdit) {
			if (this.currentUser.showBoardTransfer) {
				const isAgencyUserInClientSpace = this.currentUser.isAgencyUserInClientSpace;
				options.push({
					text: 'Copy to account',
					action: 'transferBoard',
					icon: 'Share',
				});

				if (isAgencyUserInClientSpace) {
					options.push({
						text: 'Copy to organization',
						action: 'transferBoardToAgency',
						icon: 'Share',
					});
				}
			}
		}

		options.push({
			text: this.hidden ? 'Make visible on mobile' : 'Hide on mobile',
			action: 'toggleMobileVisibility',
			icon: this.hidden ? 'DeviceMobile' : 'MobileHidden',
		});

		if (!this.isDemoAccount && canEdit) {
			options.push({
				text: 'Change owner',
				action: 'openChangeOwnerPopup',
				icon: 'User',
			});
		}

		// if (this.canEditPermissions) {
		// 	options.push({
		//		icon: 'UserAccess',
		// 		text: 'User access',
		// 		action: 'openBoardPermissions',
		// 	});
		// }

		if (this.currentUserService.isAdmin) {
			options.push(this.mmActivityLogsService.getObjectForOptionsPopup<Action>());
		}

		if (canEdit) {
			options.push({
				class: 'delete',
				text: 'Delete',
				action: 'deleteBoard',
				icon: 'Trash',
			});
		}

		return options;
	}

	// ----------- Permissions -----------

	@cached
	get userObjs() {
		const users = this.users;
		return this.mmUsersService.savedRecords.filter((user) => {
			return users.includes(+user.id!);
		});
	}

	get haveBasicPermissions(): boolean {
		return this.currentUser.isAdmin || this.isMine;
	}

	get permissionsEveryone(): boolean {
		return this.mmUsersService.savedRecords.length === this.userObjs.length;
	}

	get permissionsJustMe(): boolean {
		const userObjs = this.userObjs;
		return userObjs.length === 1 && userObjs[0]?.id === this.currentUser.model?.id;
	}

	get permissionColor(): string {
		let color = '';
		if (this.permissionsEveryone) {
			color = 'green';
		} else if (this.permissionsJustMe) {
			color = 'red';
		} else if (this.userObjs.length || !this.haveBasicPermissions) {
			color = 'orange';
		}
		return color;
	}

	get permissionIcon(): IconName | undefined {
		let icon: IconName | undefined;
		if (this.permissionsEveryone) {
			icon = 'Company';
		} else {
			const userObjs = this.userObjs;
			if (userObjs.length === 1 || !this.haveBasicPermissions) {
				icon = 'User';
			} else if (userObjs.length > 1) {
				icon = 'Team';
			}
		}
		return icon;
	}

	get permissionText(): string {
		if (!this.haveBasicPermissions) {
			return 'Shared with me';
		}

		if (this.permissionsEveryone) {
			return 'All users';
		}

		const userObjs = this.userObjs,
			myId = this.currentUser.model?.id,
			isMe = myId && userObjs.map((item) => item.id).includes(myId);

		let pplCount = userObjs.length,
			addXOthers = false,
			text = '';

		if (isMe) {
			pplCount--;
			if (pplCount) {
				text = 'Me & ';
				addXOthers = pplCount > 1;
			} else {
				text = 'Only me';
			}
		}
		if (pplCount && !addXOthers) {
			// get name of first other recipient
			let tmpUser = userObjs[0];
			if (tmpUser?.id === myId) {
				tmpUser = userObjs[1];
			}
			text += tmpUser?.name;
			pplCount--;
			if (pplCount) {
				text += ' & ';
				addXOthers = true;
			}
		}
		if (addXOthers) {
			text += pplCount + ' other' + (pplCount > 1 ? 's' : '');
		}
		if (!text) {
			text = 'nobody';
		}
		return text;
	}

	get permissionTooltip(): string {
		return this.userObjs.map((item) => item.name).join('<br />');
	}

	// ----------- Methods -----------

	checkIsWallFilled(): boolean {
		const wallObj = new DatawallGrid();
		wallObj.initGridWithoutDOM(this.blockObjs);
		return wallObj.findFreeSpace() === null;
	}

	checkIsAllMetricSetupsSpecific(): boolean {
		// false if any of metricsettings has a generic key
		return !this.blockObjs.some((block) => {
			return block.dataSelects.some((dataSelect) => {
				return !dataSelect.metricSetupSpaceAccessId;
			});
		});
	}

	override save(force = false): Promise<BoardRecord> {
		// for board template event tracking
		const changedProps: Record<string, boolean> = {};

		if (this.templateRecord) {
			const _changedProps = this.dirtyProperties;

			if (_changedProps.includes('name')) {
				changedProps.nameChanged = true;
			}

			if (_changedProps.includes('color')) {
				changedProps.colorChanged = true;
			}

			if (_changedProps.includes('themeId')) {
				changedProps.themeChanged = true;
			}

			if (_changedProps.includes('logo')) {
				if (this.logo) {
					changedProps.logoAdded = true;
				} else {
					changedProps.logoRemoved = true;
				}
			}

			if (_changedProps.includes('bg')) {
				if (this.bg) {
					changedProps.bgAdded = true;
				} else {
					changedProps.bgRemoved = true;
				}
			}

			if (_changedProps.includes('access')) {
				changedProps.accessChanged = true;
			}

			if (_changedProps.includes('tags')) {
				changedProps.tagsChanged = true;
			}
		}

		return super.save(force).then((board) => {
			const template = this.templateModel;
			if (this.isAccountTemplate && template) {
				this.mmTemplatesService.trackEvent(
					template,
					TrackingEvent.TEMPLATE_EDITED,
					changedProps,
				);
			}
			return board as BoardRecord;
		});
	}

	getDatawallUrlWithPreviewHash(previewHash: BoardPreviewMap = {}, isIframe = false): string {
		return this.mmBoardsService.getUrlWithPreviewHash(this.datawallUrl, previewHash, isIframe);
	}

	getInternalUrlWithPreviewHash(previewHash: BoardPreviewMap = {}, isIframe = false): string {
		return this.mmBoardsService.getUrlWithPreviewHash(this.internalUrl, previewHash, isIframe);
	}

	editBoard(): void {
		this.navigationService.transitionToRoute('designer.board', this.model);
	}

	openFullscreenView(): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		window.open(this.internalUrl);
	}

	openChangeOwnerPopup(): void {
		this.mmUsersService.openUserSelectPopup({
			title: 'Dashboard owner',
			text: 'Select an owner for dashboard:',
			currentUserId: String(this.model.ownerId),
			onSelect: (userId: string | null) => {
				this.model.ownerId = userId ? +userId : null;
				this.model
					.save()
					.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
			},
		});
	}

	openBoardPermissions(): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.analyticsService.event(TrackingEvent.DATABOARD_PERMISSIONS_POPUP, {
			databoard_id: this.id,
		});
		this.mmBoardsService.openBoardAccessTypePopup(this);
	}

	openWallShareSettings(
		origin: BoardsTrackingOriginType,
		previewHash = '',
		selectedTab = BoardPopupShareTab.LINK,
	): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.analyticsService.event(TrackingEvent.DATABOARD_SHARE_POPUP_OPEN, {
			origin,
			type: 'databoard',
		});
		this.mmBoardsService.openSharePopup({
			model: this,
			previewHash: { [this.id!]: previewHash },
			selectedTab,
			trackingOrigin: origin,
		});
	}

	toggleFavorite(trackingOrigin: TrackingOriginForFavoriteBoardAdded): void {
		if (this.isNew) {
			return;
		}
		const layer = this.infolayer.loading('Saving...');

		this.favorite = !this.favorite;
		if (this.favorite) {
			this.analyticsService.event(TrackingEvent.FAVORITE_BOARD_ADDED, {
				origin: trackingOrigin,
				name: this.name,
				datasources: this.specificSourceTypesKeys,
			});
		}

		// Update order to maintain favorites at top
		this.mmBoardsService
			.updateOrderAfterFavoriteToggle()
			.then(() => this.save())
			.then(
				(board: BoardRecord) => {
					layer.ok(
						`Dashboard <strong>${board.name}</strong> ${board.favorite ? '' : 'un'}favorited${board.favorite ? ' and moved to the top' : ' and removed from the top'}!`,
					);
				},
				(errorData: RequestError) => {
					layer.close();
					throw errorData;
				},
			)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	copyBoard(
		trackOrigin: TrackingOriginForDataboardDuplicate,
		askRedirect = false,
		onCopy?: (id: string) => void,
	): void {
		if (this.billing.checkIfCan(BillingFeature.BOARDS, true)) {
			//does board have at least 1 datasource
			if (this.sourcesRecords.filter((item) => item.type).length > 0) {
				this.mmBoardsService.openDuplicatePopup(
					this,
					askRedirect,
					trackOrigin,
					(createdBoard) => {
						this.mmBoardsService.openPreview(createdBoard, 'databoards');
					},
				);
			} else if (typeof onCopy === 'function') {
				this.analyticsService.event(TrackingEvent.DATABOARD_DUPLICATE, {
					origin: trackOrigin,
				});
				onCopy(this.id!);
			}
		}
	}

	deleteObject(
		trackingOrigin?: TrackingOriginForDataboardDelete,
		cb?: () => number,
		permanently = false,
	): void {
		const id = this.id,
			affirmativeFunc = () => {
				const layer = this.infolayer.loading(`Deleting dashboard...`);

				this.requestService
					.api<RecordData.Payload>('DELETE', '/boards/' + id)
					.then(
						(reply) => {
							this.analyticsService.event(TrackingEvent.DATABOARD_DELETE, {
								databoard_id: id,
								origin: trackingOrigin,
							});

							if (!this.deleted) {
								this.flowPromotionsService.startRecentlyDeletedTourForBoards();
							}

							let waitBeforeRemove = 0;

							if (typeof cb === 'function') {
								waitBeforeRemove = cb();
							}

							if (id) {
								this.mmBoardsService.markBoardNotRequested(id);
								this.garbageCollector.clearConnectedRecords('board', { id });
							}

							later(() => {
								void this.delete(true);
								this.storeService.pushPayload(reply);
							}, waitBeforeRemove);

							layer.ok(`Dashboard <strong>${this.name}</strong> deleted!`);
						},
						(errorData: RequestError) => {
							layer.close();
							throw errorData;
						},
					)
					.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
			};

		if (id && this.mmBoardsService.isBoardDataRequested(id) && this.blocks.length === 0) {
			affirmativeFunc();
			return;
		}

		this.mmBoardsService.openBoardDeleteConfirmPopup({
			onConfirm: affirmativeFunc,
			title: `Are you sure you want to delete "${this.name}" dashboard${permanently ? ' permanently' : ''}?`,
			text: permanently
				? '<strong>This cannot be undone.</strong>'
				: `<strong>"${this.name}" will be moved to Recently deleted and deleted permanently after 30 days.</strong><br><br>Note that this dashboard will be removed from all loops, reports and home widgets, and all related notifications will be deleted. These will not be restored even if you restore the dashboard.<br><br>`,
			confirmAction: 'Delete',
			cancelAction: 'Cancel',
			boardId: this.id!,
		});
	}

	transferBoard(): void {
		this.mmBoardsService
			.openTransferPopup(this)
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	changeAccess(newAccess: number, usersPermissionIds: number[] = []): boolean {
		if (this.accountService.handleDemo()) {
			return true;
		}

		let selectedUsers: number[] = [];

		switch (newAccess) {
			case UserAccess.PRIVATE:
				if (!this.isMine) {
					this.infolayer.error('You can set to private only boards you have created!');
					return false;
				}
				break;
			case UserAccess.RESTRICTED:
				selectedUsers = usersPermissionIds;
				break;
			case UserAccess.PUBLIC:
				break;
			default:
				this.loggerService.logError('Unrecognized board access!', {
					boardId: this.id,
					boardAccess: newAccess,
				});
				return false;
		}

		this.access = newAccess;

		this.users = selectedUsers;

		return true;
	}

	openAccountTemplateEditPopup(trackingOrigin: TrackingOriginForTemplateCreated): void {
		this.mmBoardsService.openAccountTemplateEditPopup(this, trackingOrigin);
	}

	openPublicTemplateEditPopup(): void {
		this.mmBoardsService.openPublicTemplateEditPopup(this);
	}

	override delete(skipBE = false): Promise<unknown> {
		let promise;

		// to handle soft delete
		// when skipBE is true and deleteRecord/ApplicationSerializer is used the model stays in storage marked as "isDeleted"
		// because of this later push of the same soft deleted board with deleted property is not pushed to store
		if (!this.deleted) {
			this.directPropUpdate({ deleted: moment().unix() });

			promise = Promise.resolve();
		} else {
			promise = super.delete(skipBE);
		}

		return promise;
	}

	// ----------- Static -----------

	static maxBlocks = 32;

	static snapshotResize(url: string, size: string): string {
		const chunks = (url || '').split('/'),
			filename = chunks.pop();
		return chunks.join('/') + '/' + size + '/' + filename;
	}

	static getFontsizeFromWidth(width: number): number {
		const baseWidth = 1400, // at what px has same block sizes as on phone with 100% zoom
			zoomLevel = width / baseWidth;
		return zoomLevel * 10;
	}

	static override modelName = ModelName.BOARD; // store record type
}
